import { useEffect, useState, useCallback } from 'react';
import { adminHeaders } from '../../lib/adminAuth';

import { tx } from '../../lib/adminI18n';
// ------------------------------------------------------------
// 管理画面: チェックイン受付端末（iPad）セットアップ  /admin/checkin-device
//   設計: .plan/checkin_device_facility_mode_design.md
//   施設を選び「このiPadに設定」→ /checkin?facility={prefix} のURLを生成。
//   そのURLをiPadのホーム画面にApp Iconとして追加して運用する。
//   端末トークンは発行しない（物理設置で担保）。施設の紐付けはURLパラメータのみ。
// ------------------------------------------------------------

interface Property {
  id: string;
  name: string;
  booking_prefix: string | null;
  is_active: number;
}

function DeviceSetupPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [props, setProps] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Property | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/properties', { headers: adminHeaders() });
      const d = await res.json().catch(() => ([]));
      const list: Property[] = Array.isArray(d) ? d : [];
      // 有効 かつ 施設コード（booking_prefix）設定済みのみ対象
      setProps(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const origin = window.location.origin;
  const checkinUrl = selected?.booking_prefix
    ? `${origin}/checkin?facility=${encodeURIComponent(selected.booking_prefix)}`
    : '';

  const copyUrl = async () => {
    if (!checkinUrl) return;
    try {
      await navigator.clipboard.writeText(checkinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* クリップボード不可環境は無視（手動コピー可） */ }
  };

  const active = props.filter((p) => p.is_active);
  const ready = active.filter((p) => p.booking_prefix && p.booking_prefix.trim());
  const notReady = active.filter((p) => !p.booking_prefix || !p.booking_prefix.trim());

  return (
    <div className={embedded ? '' : 'admin-page'}>
      {embedded
        ? <h2 className="set-section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>{tx('dev.0')}</h2>
        : <h1 className="admin-page-title">{tx('dev.0')}</h1>}

      <p style={{ color: '#475569', lineHeight: 1.8, margin: '0 0 20px', maxWidth: 720 }}>
        {tx('dev.1')}
        {tx('dev.2')}
        {tx('dev.3')}
        {tx('dev.4')}
      </p>

      {loading ? (
        <p>{tx('dev.5')}</p>
      ) : (
        <>
          {/* 施設選択 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 24 }}>
            {ready.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setSelected(p); setCopied(false); }}
                style={{
                  textAlign: 'left', padding: '16px 18px', borderRadius: 12, cursor: 'pointer',
                  border: selected?.id === p.id ? '2px solid #16a34a' : '1px solid #cbd5e1',
                  background: selected?.id === p.id ? '#f0fdf4' : '#fff',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 6 }}>{p.name}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  {tx('dev.6')} <span style={{ fontWeight: 700, color: '#16a34a' }}>{p.booking_prefix}</span>
                </div>
              </button>
            ))}
          </div>

          {ready.length === 0 && (
            <p style={{ color: '#b45309', background: '#fffbeb', borderRadius: 8, padding: '12px 16px' }}>
              {tx('dev.7')}
              {tx('dev.8')}
            </p>
          )}

          {/* 接頭辞未設定の施設の注記 */}
          {notReady.length > 0 && (
            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 24px' }}>
              {tx('dev.9')}{' '}
              {notReady.map((p) => p.name).join(tx('dev.10'))}
            </p>
          )}

          {/* 生成URL */}
          {selected && checkinUrl && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px', background: '#f8fafc', maxWidth: 720 }}>
              <h2 style={{ fontSize: 16, margin: '0 0 6px', color: '#0f172a' }}>
                {selected.name} {tx('dev.11')}
              </h2>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>
                {tx('dev.12')}
              </p>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                background: '#fff', border: '1px solid #cbd5e1', borderRadius: 10, padding: '12px 14px', marginBottom: 14,
              }}>
                <code style={{ fontSize: 15, color: '#0f172a', wordBreak: 'break-all', flex: 1 }}>{checkinUrl}</code>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <a
                  href={checkinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block', background: '#16a34a', color: '#fff', fontWeight: 700,
                    padding: '12px 20px', borderRadius: 10, textDecoration: 'none',
                  }}
                >
                  {tx('dev.13')}
                </a>
                <button
                  type="button"
                  onClick={copyUrl}
                  style={{
                    background: '#fff', color: '#334155', border: '1px solid #cbd5e1', fontWeight: 700,
                    padding: '12px 20px', borderRadius: 10, cursor: 'pointer',
                  }}
                >
                  {copied ? tx('dev.14') : tx('dev.15')}
                </button>
              </div>

              <div style={{ marginTop: 18, fontSize: 13, color: '#475569', lineHeight: 1.9 }}>
                <strong style={{ color: '#0f172a' }}>{tx('dev.16')}</strong>
                <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                  <li>{tx('dev.17')}</li>
                  <li>{tx('dev.18')}</li>
                  <li>{tx('dev.19')}</li>
                </ol>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DeviceSetupPage;
